import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowPathIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { canAccessAdministration } from '../constants/roles';
import { useSubscription } from '../hooks/useSubscription';
import { useTemplatesNeedingAttention } from '../hooks/useTemplatesNeedingAttention';
import { useUnreadMessagesCount } from '../hooks/useUnreadMessagesCount';
import { ENABLE_AI_DESIGN_STUDIO } from '../config';

import { TierBadge } from './subscription';
import CopilotPrinterLogo from './CopilotPrinterLogo';
import ThemeToggle from './ThemeToggle';

const SidebarNavigation = ({ isOpen, onToggle }) => {
  const { user, isUserAuthenticated, logout } = useAuth();
  const { hasFeature } = useSubscription();
  const { count: templatesNeedingAttention } = useTemplatesNeedingAttention();
  const unreadMessages = useUnreadMessagesCount(isUserAuthenticated && hasFeature('craftflow_commerce'));
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({});

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSection = sectionId => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const mainNavItems = [
    {
      id: 'etsy',
      label: 'Etsy',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
          />
        </svg>
      ),
      hasSubmenu: true,
      gradient: 'from-orange-100 to-orange-200',
      submenu: [
        { label: 'Dashboard', path: '/etsy/dashboard' },
        { label: 'Orders', path: '/etsy/orders' },
        { label: 'Customers', path: '/etsy/customers' },
        { label: 'Analytics', path: '/etsy/analytics' },
        { label: 'Listings', path: '/etsy/listings' },
      ],
    },
    {
      id: 'mockups',
      label: 'Mockup Creator',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
      path: '/mockup-creator',
      gradient: 'from-purple-100 to-purple-200',
    },
    {
      id: 'recreate-jobs',
      label: 'Regenerate jobs',
      icon: <ArrowPathIcon className="w-5 h-5" />,
      path: '/mockups/recreate',
      gradient: 'from-purple-100 to-purple-200',
    },
    ...(ENABLE_AI_DESIGN_STUDIO
      ? [
          {
            id: 'ai-design',
            label: 'AI Design Studio',
            icon: <SparklesIcon className="w-5 h-5" />,
            path: '/ai-design',
            gradient: 'from-violet-100 to-purple-200',
          },
        ]
      : []),
    {
      id: 'shopify',
      label: 'Shopify',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      hasSubmenu: true,
      gradient: 'from-green-100 to-green-200',
      requiredFeature: 'shopify_integration',
      submenu: [
        { label: 'Dashboard', path: '/shopify/dashboard' },
        { label: 'Products', path: '/shopify/products' },
        { label: 'Orders', path: '/shopify/orders' },
        { label: 'Analytics', path: '/shopify/analytics' },
      ],
    },
    {
      id: 'craftflow-commerce',
      label: 'CraftFlow Commerce',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
      hasSubmenu: true,
      gradient: 'from-emerald-100 to-emerald-200',
      requiredFeature: 'craftflow_commerce',
      submenu: [
        { label: 'Dashboard', path: '/craftflow/dashboard' },
        { label: 'Analytics', path: '/craftflow/analytics' },
        { label: 'Products', path: '/craftflow/products' },
        { label: 'Collections', path: '/craftflow/collections' },
        { label: 'Orders', path: '/craftflow/orders' },
        // Gang Sheet Orders, Builder Settings, Gallery Assets, and Export Jobs are now
        // consolidated as tabs inside "Gangsheet Builder" (their standalone routes remain).
        { label: 'Gangsheet Builder', path: '/craftflow/gangsheet-builder' },
        { label: 'Customers', path: '/craftflow/customers' },
        {
          label: 'Messages',
          path: '/craftflow/messages',
          badge: unreadMessages,
          badgeTitle: unreadMessages ? `${unreadMessages} unread message(s)` : null,
        },
        { label: 'Discounts', path: '/craftflow/discounts' },
        { label: 'Sales', path: '/craftflow/sales' },
        { label: 'Gift Cards', path: '/craftflow/gift-cards' },
        { label: 'Wholesale Pricing', path: '/craftflow/price-lists' },
        { label: 'Wholesale Prospects', path: '/craftflow/prospects' },
        { label: 'Invoices', path: '/craftflow/invoices' },
        { label: 'Reviews', path: '/craftflow/reviews' },
        // Pages hosts Pages + Redirects as tabs; Emails hosts Templates, Campaigns,
        // Subscribers, Logs and Analytics as tabs. One entry each instead of seven —
        // NavItem only renders one level of submenu (`hasSubmenu && !isSubmenuItem`),
        // so a nested group would never expand, and seven flat entries drowned the menu.
        { label: 'Pages', path: '/craftflow/pages' },
        { label: 'Emails', path: '/craftflow/emails' },
        { label: 'Marketing Assets', path: '/craftflow/marketing/assets' },
        { label: 'Storefront Settings', path: '/craftflow/settings' },
      ],
    },
    {
      id: 'account',
      label: 'Settings',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      path: '/account',
      gradient: 'from-sage-100 to-sage-200',
      badge: templatesNeedingAttention || 0,
      badgeTitle: templatesNeedingAttention
        ? `${templatesNeedingAttention} template(s) missing a real price — set one before uploading new listings`
        : null,
    },
  ];

  const isActiveLink = path => {
    // Handle tab-based routes like /?tab=overview
    if (path.includes('?')) {
      const [pathPart, queryPart] = path.split('?');
      if (location.pathname !== pathPart) return false;

      const urlParams = new URLSearchParams(location.search);
      const pathParams = new URLSearchParams(queryPart);

      // Check if all params in the nav path match current URL
      for (const [key, value] of pathParams.entries()) {
        if (urlParams.get(key) !== value) return false;
      }
      return true;
    }

    // Handle regular routes
    if (path === '/') {
      return location.pathname === '/' && !location.search;
    }
    return location.pathname.startsWith(path);
  };

  const NavItem = ({ item, isSubmenuItem = false }) => {
    const isActive = isActiveLink(item.path || '/');
    const hasSubmenu = item.hasSubmenu && !isSubmenuItem;
    const isExpanded = expandedSections[item.id];

    const baseClasses = `
      flex items-center w-full px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200
      ${isSubmenuItem ? 'ml-4 pl-8' : ''}
    `;

    const activeClasses = isActive
      ? `bg-gradient-to-r ${item.gradient || 'from-lavender-100 to-lavender-200'} text-lavender-800 shadow-xs`
      : 'text-sage-600 dark:text-gray-400 hover:text-sage-800 hover:bg-sage-50 dark:hover:bg-gray-800';

    const content = (
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center space-x-3">
          <div className={`${isActive ? 'text-lavender-600' : 'text-sage-500 dark:text-gray-400'}`}>{item.icon}</div>
          <span className="truncate">{item.label}</span>
        </div>

        <div className="flex items-center space-x-2">
          {item.badge > 0 && (
            <span
              title={item.badgeTitle || `${item.badge} item(s) need attention`}
              className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold dark:bg-amber-900/40 dark:text-amber-200"
            >
              {item.badge}
            </span>
          )}
          {hasSubmenu && (
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
        </div>
      </div>
    );

    if (hasSubmenu) {
      return (
        <div>
          <button onClick={() => toggleSection(item.id)} className={`${baseClasses} ${activeClasses}`}>
            {content}
          </button>

          {isExpanded && (
            <div className="mt-1 space-y-1">
              {item.submenu.map((subItem, index) => (
                <NavItem key={index} item={subItem} isSubmenuItem={true} />
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link to={item.path} className={`${baseClasses} ${activeClasses}`} onClick={() => onToggle && onToggle(false)}>
        {content}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div className="fixed inset-0 bg-black/25 z-40 lg:hidden" onClick={() => onToggle(false)} />
      )}

      {/* Sidebar */}
      <div
        className={`
        fixed left-0 top-0 h-full flex flex-col bg-gradient-to-b from-white to-sage-50 dark:from-gray-800 dark:to-gray-900 border-r border-sage-200 dark:border-gray-700 z-50 transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:absolute lg:z-auto lg:h-full
        w-64 shadow-lg lg:shadow-none
      `}
      >
        {/* Header */}
        <div className="p-6 border-b border-sage-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <CopilotPrinterLogo size={32} />
              <div>
                <h1 className="font-bold text-sage-900 dark:text-gray-100 text-lg leading-none">CopilotPrinter</h1>
                <p className="text-sage-600 dark:text-gray-400 text-xs">Ecommerce Printer Automation</p>
              </div>
            </Link>

            <button
              onClick={() => onToggle(false)}
              className="lg:hidden p-1.5 rounded-lg text-sage-500 dark:text-gray-400 hover:text-sage-700 dark:hover:text-gray-300 hover:bg-sage-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 min-h-0 px-4 py-6 space-y-2 overflow-y-auto">
          {isUserAuthenticated ? (
            <>
              {mainNavItems
                .filter(item => !item.requiredFeature || hasFeature(item.requiredFeature))
                .map(item => (
                  <NavItem key={item.id} item={item} />
                ))}

              {/* Administration — super_admin ONLY. Was admin/owner/super_admin, which
                  put platform-operations tools (shop reset, user management, quota)
                  in front of every shop owner. The route and the /admin/* routers
                  enforce the same rule, so hiding this is presentation, not the gate. */}
              {canAccessAdministration(user?.role) && (
                <div className="pt-4 border-t border-sage-200 dark:border-gray-700">
                  <p className="px-3 text-xs font-semibold text-sage-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    Administration
                  </p>
                  <NavItem
                    item={{
                      id: 'admin',
                      label: 'Admin Dashboard',
                      icon: (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      ),
                      path: '/admin',
                      gradient: 'from-red-100 to-red-200',
                    }}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-sage-600 dark:text-gray-400 text-sm mb-4">Please log in to access navigation</p>
              <Link
                to="/login"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-lavender-400 to-lavender-500 text-white rounded-lg hover:from-lavender-500 hover:to-lavender-600 transition-all duration-200"
              >
                Sign In
              </Link>
            </div>
          )}
        </nav>

        {/* Theme toggle — always visible so guests can flip dark mode too */}
        <div className="px-4 pt-3 pb-2 border-t border-sage-200 dark:border-gray-700 flex items-center justify-between">
          <span className="text-xs font-medium text-sage-700 dark:text-gray-300">Theme</span>
          <ThemeToggle />
        </div>

        {/* User Profile Section */}
        {isUserAuthenticated && (
          <div className="p-4 border-t border-sage-200 dark:border-gray-700">
            {/* User Profile */}
            <div className="flex items-center space-x-3 p-3 rounded-xl bg-gradient-to-r from-sage-50 to-mint-50 dark:from-gray-800 dark:to-gray-800">
              <div className="w-8 h-8 bg-gradient-to-br from-mint-400 to-mint-500 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-sm">
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sage-900 dark:text-gray-100 font-medium text-sm truncate">
                  {user?.shop_name || 'User'}
                </p>
                <div className="flex items-center space-x-2">
                  <p className="text-sage-600 dark:text-gray-400 text-xs truncate">
                    {user.email || 'user@example.com'}
                  </p>
                  <TierBadge size="small" />
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="p-1.5 text-sage-500 dark:text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all duration-200"
                title="Sign out"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SidebarNavigation;
